import { Injectable } from '@angular/core';

export interface PaymentAttemptState {
  contributionId: string;
  tripId: string;
  initiatedAt: number;
}

/**
 * Service to manage payment attempt state in sessionStorage.
 * Used to handle multi-window scenarios (e.g., Arc browser opening Stripe in a new window).
 *
 * SECURITY FIX: Changed from localStorage to sessionStorage to:
 * - Reduce XSS attack surface (data doesn't persist across sessions)
 * - Clear sensitive payment data when browser/tab is closed
 * - Prevent data from being accessed across browser tabs (isolation)
 */
@Injectable({ providedIn: 'root' })
export class PaymentStateService {
  private readonly STORAGE_KEY = 'gt_payment_attempt';
  private readonly EXPIRATION_MS = 30 * 60 * 1000; // 30 minutes

  /**
   * Save payment attempt state before redirecting to Stripe.
   */
  savePaymentAttempt(contributionId: string, tripId: string): void {
    const state: PaymentAttemptState = {
      contributionId,
      tripId,
      initiatedAt: Date.now(),
    };
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  /**
   * Get the current payment attempt state.
   * Returns null if no attempt exists or if it has expired.
   */
  getPaymentAttempt(): PaymentAttemptState | null {
    const stored = sessionStorage.getItem(this.STORAGE_KEY);
    if (!stored) return null;

    try {
      const state: PaymentAttemptState = JSON.parse(stored);

      // Check expiration (30 minutes)
      if (Date.now() - state.initiatedAt > this.EXPIRATION_MS) {
        this.clearPaymentAttempt();
        return null;
      }

      return state;
    } catch {
      this.clearPaymentAttempt();
      return null;
    }
  }

  /**
   * Clear the payment attempt state.
   * Called after successful payment or when resetting the wizard.
   */
  clearPaymentAttempt(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }
}
